

import { useState } from "react";
import { useRouter } from '@/lib/navigation';
import { ArrowLeft, ChevronDown, ChevronUp } from "lucide-react";
import Badge from "../../components/Badge";
import { EventDetail } from "./types";

interface EventHeaderProps {
  event: EventDetail;
  isProjected?: boolean;
}

export default function EventHeader({ event, isProjected }: EventHeaderProps) {
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      {/* Main Header - Always visible */}
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => router.push('/events')}
            className="w-10 h-10 rounded-xl bg-stone-100 text-stone-500 hover:bg-stone-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-bold text-stone-800 text-lg sm:text-xl">{event.name}</h1>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-stone-500">
              <span>Event #{event.id}</span>
              <span>•</span>
              <span className="capitalize">{event.event_type}</span>
              {isProjected && (
                <span className="text-amber-600 text-xs bg-amber-50 px-2 py-0.5 rounded-full">Projected</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge label={event.status} status={event.status} />
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-8 h-8 rounded-lg bg-stone-100 text-stone-500 hover:bg-stone-200 flex items-center justify-center transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Collapsible Details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-stone-100">
          <div className="pt-3 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-stone-500">Client:</span>
              <span className="font-medium text-stone-800">{event.client?.name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Date:</span>
              <span className="font-medium text-stone-800">{event.start_date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-500">Guests:</span>
              <span className="font-medium text-stone-800">{event.guest_count || '-'}</span>
            </div>
            {event.venue && (
              <div className="flex justify-between">
                <span className="text-stone-500">Venue:</span>
                <span className="font-medium text-stone-800">{event.venue.name}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
